// frontend/src/pages/Businesstype/BusinessTypeList.jsx
import React, { useEffect, useState } from "react";
import { Edit, Trash2, Plus, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./BusinesstypeList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BusinessTypeList = () => {
  const { user } = useAuth();
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBusinessTypes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/businesstype`, {
        headers: getAuthHeaders(),
      });

      setBusinessTypes(res.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch business types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this business type?")) {
      try {
        await axios.delete(`${url}/api/businesstype/${id}`, {
          headers: getAuthHeaders(),
        });
        toast.success("Business type deleted successfully");
        fetchBusinessTypes();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete business type");
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading business types...</div>
      </div>
    );
  }

  return (
    <div className="businesstype-container">
      <div className="header">
        <div>
          <h2>Business Type List</h2>
          <p className="info">
            Showing all business types in the system
          </p>
        </div>
        <button onClick={() => navigate("/AddBusinesstype")} className="add-button">
          <Plus size={16} /> Add Business Type
        </button>
      </div>

      <div className="stats-bar">
        <span>Total: {businessTypes.length} business types</span>
        <span>Created by: {user?.name || "You"}</span>
      </div>

      <table className="businesstype-table">
        <thead>
          <tr>
            <th>Business Type Name</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Last Updated</th>
            <th>Status</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {businessTypes.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-data">
                No business types found. Create your first business type!
              </td>
            </tr>
          ) : (
            businessTypes.map((businessType) => (
              <tr key={businessType._id}>
                <td>
                  <div className="businesstype-name">
                    <strong>{businessType.name}</strong>
                  </div>
                </td>
                <td>
                  <div className="user-info">
                    <User size={12} />
                    <span>
                      {businessType.created_by?.name ||
                       businessType.created_by?.email ||
                       "System"}
                    </span>
                  </div>
                </td>
                <td>
                  {new Date(businessType.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
                <td>
                  {businessType.updated_at
                    ? new Date(businessType.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : "Never"
                  }
                </td>
                <td>
                  <span className={`status-badge ${businessType.status}`}>
                    {businessType.status}
                  </span>
                </td>
                <td className="actions">
                  <button
                    onClick={() => navigate(`/businesstype/edit/${businessType._id}`)}
                    className="edit-btn"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(businessType._id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BusinessTypeList;